/**
 * Attachment service.
 *
 * Wraps the Railway routes that replaced the prior client-side
 * `attachmentService`. The exported surface mirrors the prior service 1:1
 * so call sites don't change. Uploads go through Fastify's multipart
 * handler; downloads route through Railway for consistency.
 */

import {
  ApiError,
  apiGet,
  apiPatch,
  apiPostMultipart,
  apiPost,
  apiDelete,
} from "@/lib/apiClient";
import type {
  CreateResult,
  DeleteResult,
  ListResult,
  ServiceContext,
  UpdateResult,
} from "@prolarus/shared";

export interface Attachment {
  id: string;
  company_id: string;
  entity_type: string;
  entity_id: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  attachment_role: string | null;
  notes: string | null;
  created: string;
  modified: string;
  created_by: string | null;
  updated_by: string | null;
  created_by_profile?: { full_name: string | null } | null;
}

export interface AttachmentInsert {
  entity_type: string;
  entity_id: string;
  file_name: string;
  file_path: string;
  file_size?: number | null;
  mime_type?: string | null;
  attachment_role?: string | null;
  notes?: string | null;
}

export interface AttachmentUpdate {
  attachment_role?: string | null;
  notes?: string | null;
}

function toError(err: unknown): Error {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) return err;
  return new Error(String(err));
}

export const attachmentService = {
  async listByEntity(entityType: string, entityId: string): Promise<ListResult<Attachment>> {
    try {
      const params = new URLSearchParams({ entityType, entityId });
      const result = await apiGet<{ data: Attachment[] }>(
        `/api/attachments?${params.toString()}`,
      );
      return { data: result.data, error: null };
    } catch (err) {
      return { data: [], error: toError(err) };
    }
  },

  async listByEntityWithRole(
    entityType: string,
    entityId: string,
    role: string,
  ): Promise<ListResult<Attachment>> {
    try {
      const params = new URLSearchParams({ entityType, entityId, role });
      const result = await apiGet<{ data: Attachment[] }>(
        `/api/attachments?${params.toString()}`,
      );
      return { data: result.data, error: null };
    } catch (err) {
      return { data: [], error: toError(err) };
    }
  },

  async listByEntities(
    entities: Array<{ entityType: string; entityIds: string[] }>,
  ): Promise<ListResult<Attachment>> {
    try {
      const result = await apiPost<{ data: Attachment[] }>(
        "/api/attachments/by-entities",
        { entities },
      );
      return { data: result.data, error: null };
    } catch (err) {
      return { data: [], error: toError(err) };
    }
  },

  async create(
    file: File,
    entityType: string,
    entityId: string,
    _context: ServiceContext,
    options?: { attachmentRole?: string; notes?: string },
  ): Promise<CreateResult<Attachment>> {
    try {
      const formData = new FormData();
      formData.append("file", file, file.name);
      formData.append("entity_type", entityType);
      formData.append("entity_id", entityId);
      if (options?.attachmentRole) {
        formData.append("attachment_role", options.attachmentRole);
      }
      if (options?.notes) {
        formData.append("notes", options.notes);
      }
      const result = await apiPostMultipart<{ data: Attachment }>(
        "/api/attachments",
        formData,
      );
      return { data: result.data, error: null };
    } catch (err) {
      return { data: null, error: toError(err) };
    }
  },

  async update(
    id: string,
    data: AttachmentUpdate,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ): Promise<UpdateResult<Attachment>> {
    try {
      const result = await apiPatch<{ data: Attachment; changesLogged?: boolean }>(
        `/api/attachments/${encodeURIComponent(id)}`,
        data,
      );
      return {
        data: result.data,
        error: null,
        changesLogged: !!result.changesLogged,
      };
    } catch (err) {
      return { data: null, error: toError(err), changesLogged: false };
    }
  },

  async delete(
    id: string,
    _filePath: string,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ): Promise<DeleteResult> {
    try {
      const result = await apiDelete<{ success: boolean; changesLogged?: boolean }>(
        `/api/attachments/${encodeURIComponent(id)}`,
      );
      return {
        success: result.success,
        error: null,
        changesLogged: !!result.changesLogged,
      };
    } catch (err) {
      return { success: false, error: toError(err), changesLogged: false };
    }
  },

  async downloadAsBase64(
    filePath: string,
  ): Promise<{ data: string | null; error: Error | null }> {
    try {
      const params = new URLSearchParams({ file_path: filePath });
      const result = await apiGet<{ data: string }>(
        `/api/attachments/download-base64?${params.toString()}`,
      );
      return { data: result.data, error: null };
    } catch (err) {
      return { data: null, error: toError(err) };
    }
  },

  async getSignedUrl(
    filePath: string,
    expiresIn = 3600,
  ): Promise<{ url: string | null; error: Error | null }> {
    try {
      const params = new URLSearchParams({
        file_path: filePath,
        expires_in: String(expiresIn),
      });
      const result = await apiGet<{ url: string }>(
        `/api/attachments/signed-url?${params.toString()}`,
      );
      return { url: result.url, error: null };
    } catch (err) {
      return { url: null, error: toError(err) };
    }
  },
};
